import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, Subject, takeUntil } from 'rxjs'
import { SHADER_COLORS } from '../../../shared/utils/shaders'
import { GenericLayer } from '../../../shared/model/layers/generic-layer'
import { Store } from '@ngrx/store'
import {
    selectAllSavedHierarchicalSelectionNodeItems,
    selectAllSavedUnsupervisedSelectionNodeItems,
    selectSavedAndCurrentFreeSelectionsAsLayers,
    selectSavedAndCurrentHierarchicalSelectionsAsLayers,
    selectSavedAndCurrentUnsupervisedSelectionsAsLayers,
} from '../../../state/selection/selection.selectors'
import { layerControlsVisible$ } from '../../../shared/utils/layer-controls-utils'
import { CdkPortal } from '@angular/cdk/portal'
import { Overlay, OverlayConfig, OverlayRef, PositionStrategy } from '@angular/cdk/overlay'
import { DOCUMENT } from '@angular/common'
import {
    selectShowHierarchicalSelectionLayers,
    selectShowSelectionLayers,
    selectShowUnsupervisedSelectionLayers,
} from '../../../state/combined-selectors'
import { ViewerLayersService } from '../../neuroglancer-frame/viewer-layers.service'
import { SelectionLayer } from 'src/app/shared/model/layers/selection-layer'
import { SelectionTreeItem } from '../../../state/selection/selection.state'
import {
    LayerGroupReference,
    LayerGroupReferenceDeprecated,
    LayerReference,
} from './layer-groups/layer-groups.model'
import { SelectionName } from 'src/app/state/selection/selection.state'
import { selectSearchKey } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-viewer-layers',
    templateUrl: './viewer-layers.component.html',
    styleUrls: ['./viewer-layers.component.scss'],
})
export class ViewerLayersComponent implements OnInit, OnDestroy {
    @ViewChild(CdkPortal) private layerOverlayTemplate!: CdkPortal
    private layerOverlay: OverlayRef | null = null

    private readonly _detailView$ = new BehaviorSubject<boolean | null>(null)
    detailView$ = this._detailView$.asObservable()

    private readonly _detailViewLayer$ = new BehaviorSubject<GenericLayer | null>(null)
    detailViewLayer$ = this._detailViewLayer$.asObservable()

    private readonly _viewAllLayers$ = new BehaviorSubject<GenericLayer[] | null>(null)
    viewAllLayers$ = this._viewAllLayers$.asObservable()

    colorPalette = SHADER_COLORS.map(color => color.hexString)

    showSelectionLayers$: Observable<boolean>
    showHierarchicalSelectionLayers$: Observable<boolean>
    showUnsupervisedSelectionLayers$: Observable<boolean>

    private readonly _layerGroupOverlayTitle$ = new BehaviorSubject<string>('')
    layerGroupOverlayTitle$ = this._layerGroupOverlayTitle$.asObservable()

    selectionLayers$: Observable<GenericLayer[]>
    hierarchicalSelectionLayers$: Observable<SelectionLayer[]>
    hierarchicalSelectionLayers: SelectionLayer[]
    hierarchicalSelectionNodeItems$: Observable<{ groupName: string; tree: SelectionTreeItem[] }[]>
    selectionGroup$: { name: string; selections: SelectionLayer[] }[] = []
    unsupervisedSelectionLayers$: Observable<SelectionLayer[]>
    unsupervisedSelectionLayers: SelectionLayer[]
    unsupervisedSelectionNodeItems$: Observable<{ groupName: string; tree: SelectionTreeItem[] }[]>
    unsupervisedSelectionGroup$: { name: string; selections: SelectionLayer[] }[] = []
    listOpenSelectionGroups: string[] = []
    listOpenSelections: { [key: string]: string[] } = {}
    SelectionName = SelectionName

    isLoading: boolean = false

    private readonly onDestroy$ = new Subject<void>()

    constructor(
        readonly viewerLayers: ViewerLayersService,
        private readonly store: Store,
        private readonly overlay: Overlay,
        @Inject(DOCUMENT) private readonly document: Document
    ) {}

    ngOnInit(): void {
        layerControlsVisible$.pipe(takeUntil(this.onDestroy$)).subscribe(visible => {
            if (!visible) this.closeLayerDetailOverlay()
        })

        this.showSelectionLayers$ = this.store.select(selectShowSelectionLayers)
        this.showHierarchicalSelectionLayers$ = this.store.select(
            selectShowHierarchicalSelectionLayers
        )
        this.showUnsupervisedSelectionLayers$ = this.store.select(
            selectShowUnsupervisedSelectionLayers
        )

        this.selectionLayers$ = this.store.select(
            selectSavedAndCurrentFreeSelectionsAsLayers(this.store)
        )

        this.hierarchicalSelectionLayers$ = this.store.select(
            selectSavedAndCurrentHierarchicalSelectionsAsLayers(this.store)
        )

        this.hierarchicalSelectionNodeItems$ = this.store.select(
            selectAllSavedHierarchicalSelectionNodeItems
        )

        this.hierarchicalSelectionLayers$.pipe(takeUntil(this.onDestroy$)).subscribe(items => {
            this.hierarchicalSelectionLayers = items
        })

        combineLatest([this.hierarchicalSelectionNodeItems$, this.store.select(selectSearchKey)])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(([items, searchKey]) => {
                const selectionGroup: { name: string; selections: SelectionLayer[] }[] = []
                items.forEach(item => {
                    let hasMatchingLayers = false
                    const existingGroupIndex = selectionGroup.findIndex(
                        g => g.name === item.groupName
                    )
                    if (item.groupName?.toLowerCase().includes(searchKey.toLowerCase())) {
                        hasMatchingLayers = true
                    }

                    const layers = this.hierarchicalSelectionLayers.filter(
                        l => l.groupName === item.groupName
                    )
                    layers.forEach(l => {
                        if (l.name?.toLowerCase().includes(searchKey.toLowerCase())) {
                            hasMatchingLayers = true
                        }
                    })

                    if (!hasMatchingLayers) {
                        return
                    }

                    const selections = item.tree.map(i => {
                        const selection = layers.find(g => g.name === i.name)!
                        selection.setLevel(i.level)
                        return selection
                    })

                    const unsavedLayer = layers.find(l => !l.name)
                    if (unsavedLayer) {
                        selections.unshift(unsavedLayer)
                    }

                    const group = {
                        name: item.groupName!,
                        selections: selections,
                    }
                    if (existingGroupIndex >= 0) {
                        selectionGroup[existingGroupIndex] = group
                    } else {
                        selectionGroup.push(group)
                    }
                })
                this.selectionGroup$ = selectionGroup
            })

        this.unsupervisedSelectionLayers$ = this.store.select(
            selectSavedAndCurrentUnsupervisedSelectionsAsLayers(this.store)
        )

        this.unsupervisedSelectionNodeItems$ = this.store.select(
            selectAllSavedUnsupervisedSelectionNodeItems
        )

        this.unsupervisedSelectionLayers$.pipe(takeUntil(this.onDestroy$)).subscribe(items => {
            this.unsupervisedSelectionLayers = items
        })

        combineLatest([this.unsupervisedSelectionNodeItems$, this.store.select(selectSearchKey)])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(([items, searchKey]) => {
                const selectionGroup: { name: string; selections: SelectionLayer[] }[] = []
                items.forEach(item => {
                    let hasMatchingLayers = false
                    const existingGroupIndex = selectionGroup.findIndex(
                        g => g.name === item.groupName
                    )
                    if (item.groupName?.toLowerCase().includes(searchKey.toLowerCase())) {
                        hasMatchingLayers = true
                    }

                    const layers = this.unsupervisedSelectionLayers.filter(
                        l => l.groupName === item.groupName
                    )
                    layers.forEach(l => {
                        if (l.name?.toLowerCase().includes(searchKey.toLowerCase())) {
                            hasMatchingLayers = true
                        }
                    })

                    if (!hasMatchingLayers) {
                        return
                    }

                    const selections = item.tree.map(i => {
                        const selection = layers.find(g => g.name === i.name)!
                        selection.setLevel(i.level)
                        return selection
                    })

                    const unsavedLayer = layers.find(l => !l.name)
                    if (unsavedLayer) {
                        selections.unshift(unsavedLayer)
                    }

                    const group = {
                        name: item.groupName!,
                        selections: selections,
                    }
                    if (existingGroupIndex >= 0) {
                        selectionGroup[existingGroupIndex] = group
                    } else {
                        selectionGroup.push(group)
                    }
                })
                this.unsupervisedSelectionGroup$ = selectionGroup
            })
    }

    ngOnDestroy(): void {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    toggleLayerDetailOverlay($event: LayerReference): void {
        const { layer, elementId } = $event

        if (
            this._detailViewLayer$.value?.nameOrDefault === layer.nameOrDefault &&
            this._detailView$.value
        ) {
            this.closeLayerDetailOverlay()
            return
        }

        this._detailView$.next(true)
        this._detailViewLayer$.next(layer)

        const element = this.document.getElementById(elementId)
        if (!element) {
            console.error('No element to attach layer detail overlay to')
            return
        }
        this.openLayerOverlay(element)
        this.isLoading = false
    }

    closeLayerDetailOverlay(): void {
        this._detailViewLayer$.next(null)
        this.closeLayerOverlay()
    }

    toggleLayerGroupOverlay($event: LayerGroupReference | LayerGroupReferenceDeprecated): void {
        const { groupName, elementId, groupId } = $event

        if (this._layerGroupOverlayTitle$.value === groupName && !this._detailView$.value) {
            this.closeLayerGroupOverlay()
            return
        }

        if ('layers' in $event) {
            this.openLayerGroupOverlay(groupName, $event.layers, elementId)
            return
        }

        if ($event.groupId === undefined) {
            this.closeLayerGroupOverlay()
            return
        }

        this.viewerLayers
            .getLayersForGroup(groupId || groupName)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(layers => {
                this.openLayerGroupOverlay(groupName, layers, elementId)
            })
    }

    private openLayerGroupOverlay(
        groupName: string,
        layers: GenericLayer[],
        elementId: string
    ): void {
        this._detailView$.next(false)
        this._viewAllLayers$.next(layers)
        this._layerGroupOverlayTitle$.next(groupName)

        const element = this.document.getElementById(elementId)
        if (!element) {
            console.error('No element to attach layer detail overlay to')
            return
        }
        this.openLayerOverlay(element)
    }

    closeLayerGroupOverlay(): void {
        this._viewAllLayers$.next(null)
        this._layerGroupOverlayTitle$.next('')
        this.closeLayerOverlay()
    }

    private openLayerOverlay(element: HTMLElement): void {
        // todo: make positioning dynamic
        const staticPositionElement = this.document.getElementById('layer-overlay-origin')
        if (!staticPositionElement) return
        element = staticPositionElement

        if (!this.layerOverlay) {
            this.layerOverlay = this.overlay.create(
                new OverlayConfig({ positionStrategy: this.createPositionStrategy(element) })
            )
        } else {
            this.layerOverlay.updatePositionStrategy(this.createPositionStrategy(element))
        }

        if (this.layerOverlay?.hasAttached()) {
            this.layerOverlay.detach()
        }

        this.layerOverlay.attach(this.layerOverlayTemplate)
    }

    private closeLayerOverlay(): void {
        this.layerOverlay?.detach()
    }

    private createPositionStrategy(element: HTMLElement): PositionStrategy {
        return this.overlay
            .position()
            .flexibleConnectedTo(element)
            .withPush(true)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'top',
                },
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'bottom',
                },
            ])
    }

    getListOpen(groupName: string, groupType: string): boolean {
        return this.listOpenSelections[groupType]?.includes(groupName) ?? false
    }

    toggleListOpen(groupName: string, groupType: string): void {
        if (this.listOpenSelections[groupType]?.includes(groupName)) {
            this.listOpenSelections[groupType] = this.listOpenSelections[groupType].filter(
                g => g !== groupName
            )
        } else {
            if (!this.listOpenSelections[groupType]) {
                this.listOpenSelections[groupType] = []
            }
            this.listOpenSelections[groupType].push(groupName)
        }
    }

    toggleGroupListOpen(groupName: string): void {
        if (this.listOpenSelectionGroups.includes(groupName)) {
            this.listOpenSelectionGroups = this.listOpenSelectionGroups.filter(g => g !== groupName)
        } else {
            this.listOpenSelectionGroups.push(groupName)
        }
    }
}
